import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { checkApplicationStatus, type ApplicationStatusResult } from "@/lib/auth.functions";
import { Check, X } from "lucide-react";

export const Route = createFileRoute("/application-status")({
  head: () => ({
    meta: [
      { title: "Application Status — Pluggz" },
      { name: "description", content: "Check the status of your Pluggz creator application." },
      { property: "og:title", content: "Application Status — Pluggz" },
      { property: "og:url", content: `${SITE_URL}/application-status` },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/application-status` }],
  }),
  component: ApplicationStatusPage,
});

function ApplicationStatusPage() {
  const check = useServerFn(checkApplicationStatus);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApplicationStatusResult | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await check({ data: { email } });
      setResult(r);
      if (!r.ok) setError(r.error);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative mx-auto w-full max-w-md overflow-hidden px-4 py-12 sm:px-6 sm:py-20">
      <div aria-hidden className="pointer-events-none absolute -left-20 top-10 h-48 w-48 rounded-full bg-accent/20 blur-3xl float-slow sm:h-64 sm:w-64" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-20 h-48 w-48 rounded-full bg-sand/30 blur-3xl float-slow sm:h-64 sm:w-64" style={{ animationDelay: "-3s" }} />
      <div className="relative">
        <p className="editorial-eyebrow">Creator application</p>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
          Check your <span className="italic shimmer">application</span>
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Enter the email you applied with to see your status
        </p>

        <form onSubmit={submit} className="mt-8 space-y-3">
          <div>
            <label
              htmlFor="status-email"
              className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground"
            >
              Email address
            </label>
            <input
              id="status-email"
              required
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 text-base outline-none transition-colors focus:border-foreground sm:text-sm"
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <button
            disabled={loading}
            type="submit"
            className="w-full bg-foreground px-6 py-3 text-sm uppercase tracking-widest text-background transition-all duration-300 hover:scale-[1.01] disabled:opacity-50"
          >
            {loading ? "Checking…" : "Check status"}
          </button>
        </form>

        {result && result.ok && (
          <div className="mt-10">
            {result.found ? (
              <StatusDisplay status={result.status} />
            ) : (
              <p className="rounded-md border border-border bg-card/60 p-4 text-sm text-muted-foreground">
                No application found for this email address.
              </p>
            )}
          </div>
        )}

        <p className="mt-10 text-center text-sm text-muted-foreground">
          Haven't applied yet?{" "}
          <Link to="/apply" className="link-underline text-foreground">Apply now</Link>
        </p>
      </div>
    </div>
  );
}

function StatusDisplay({ status }: { status: "pending" | "approved" | "rejected" }) {
  const step2Done = status === "approved" || status === "rejected";
  const step3Done = status === "approved" || status === "rejected";
  const step3Tone: "green" | "red" | "none" =
    status === "approved" ? "green" : status === "rejected" ? "red" : "none";

  const message =
    status === "pending"
      ? "Your application is being reviewed. We'll be in touch within 5 working days."
      : status === "approved"
        ? "Congratulations! You've been approved. Check your email for next steps."
        : "Unfortunately your application was unsuccessful this time.";

  return (
    <div>
      <ol className="flex items-start">
        <Step n={1} label="Received" tone="green" filled last={false} connectorOn />
        <Step
          n={2}
          label="Under review"
          tone={step2Done ? "green" : "none"}
          filled={step2Done}
          last={false}
          connectorOn={step3Done}
        />
        <Step
          n={3}
          label="Decision made"
          tone={step3Tone}
          filled={step3Done}
          last
          connectorOn={false}
        />
      </ol>
      <p className="mt-6 text-sm text-foreground">{message}</p>
    </div>
  );
}

function Step({
  n,
  label,
  tone,
  filled,
  last,
  connectorOn,
}: {
  n: number;
  label: string;
  tone: "green" | "red" | "none";
  filled: boolean;
  last: boolean;
  connectorOn: boolean;
}) {
  const bg =
    tone === "green"
      ? "bg-emerald-600 border-emerald-600 text-white"
      : tone === "red"
        ? "bg-rose-600 border-rose-600 text-white"
        : "bg-background border-border text-muted-foreground";

  return (
    <li className={`flex flex-1 items-start ${last ? "flex-none" : ""}`}>
      <div className="flex flex-col items-center">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs transition-colors ${bg}`}
          aria-label={`${label} — ${filled ? "complete" : "pending"}`}
        >
          {filled ? (
            tone === "red" ? <X size={16} /> : <Check size={16} />
          ) : (
            n
          )}
        </div>
        <span className="mt-2 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
      </div>
      {!last && (
        <div
          className={`mx-3 mt-4 h-px flex-1 ${connectorOn ? "bg-emerald-600" : "bg-border"}`}
        />
      )}
    </li>
  );
}